import React, { useEffect, useState } from "react";
import { X, Upload, Link2 } from "lucide-react";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";

const EMPTY = {
  title: "",
  description: "",
  content: "",
  image_url: "",
  category: "",
  editor_name: "",
  published: true,
};

export default function NewsForm({ existing, onClose, onSaved }) {
  const [form, setForm] = useState(existing ? { ...existing } : EMPTY);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [imageMode, setImageMode] = useState(
    existing && existing.image_url && !existing.image_url.startsWith("/api/")
      ? "url"
      : "upload"
  );

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("image_url", data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_url) return toast.error("Please add a cover image");
    if (!form.category) return toast.error("Please pick a category");
    setBusy(true);
    try {
      if (existing) {
        await api.put(`/admin/news/${existing.id}`, form);
        toast.success("Story updated");
      } else {
        await api.post("/admin/news", form);
        toast.success("Story published");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="news-form-overlay"
      className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose}
    >
      <div
        className="bg-[#FDFBF7] w-full max-w-2xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#FDFBF7] border-b border-black/10 px-8 py-5 flex items-center justify-between z-10">
          <div>
            <p className="overline text-[#76746F]">{existing ? "Edit" : "New"} story</p>
            <h2 className="font-serif-display text-2xl mt-1">
              {existing ? form.title || "Untitled" : "Compose a new story"}
            </h2>
          </div>
          <button
            data-testid="close-form"
            onClick={onClose}
            className="w-10 h-10 rounded-full border border-black/15 hover:bg-black hover:text-white transition-colors flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="px-8 py-8 space-y-7" data-testid="news-form">
          <Field label="Title">
            <input
              data-testid="title-input"
              required
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="A headline that demands a read"
              className="w-full bg-transparent outline-none border-b border-black/20 focus:border-black py-2 font-serif-display text-2xl tracking-tight"
            />
          </Field>

          <Field label="Description">
            <textarea
              data-testid="description-input"
              required
              rows={2}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="A short, evocative summary"
              className="w-full bg-transparent outline-none border-b border-black/20 focus:border-black py-2 font-sans-modern resize-none"
            />
          </Field>

          <Field label="Full article">
            <textarea
              data-testid="content-input"
              rows={6}
              value={form.content || ""}
              onChange={(e) => update("content", e.target.value)}
              placeholder="The body of your story…"
              className="w-full bg-white border border-black/15 rounded-lg p-4 outline-none focus:border-black font-sans-modern text-sm leading-relaxed"
            />
          </Field>

          <div className="grid grid-cols-2 gap-5">
            <Field label="Category">
              <select
                data-testid="category-select"
                required
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                className="w-full bg-transparent outline-none border-b border-black/20 focus:border-black py-2 font-sans-modern"
              >
                <option value="">Select…</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Editor name">
              <input
                data-testid="editor-input"
                required
                value={form.editor_name}
                onChange={(e) => update("editor_name", e.target.value)}
                placeholder="e.g. Amara Whitfield"
                className="w-full bg-transparent outline-none border-b border-black/20 focus:border-black py-2 font-sans-modern"
              />
            </Field>
          </div>

          <div>
            <p className="overline text-[#76746F] mb-3">Cover image</p>
            <div className="flex gap-2 mb-4">
              <ModeButton active={imageMode === "upload"} onClick={() => setImageMode("upload")} icon={<Upload size={14} />} testId="mode-upload">Upload</ModeButton>
              <ModeButton active={imageMode === "url"} onClick={() => setImageMode("url")} icon={<Link2 size={14} />} testId="mode-url">From URL</ModeButton>
            </div>

            {imageMode === "upload" ? (
              <label data-testid="upload-zone" className="flex flex-col items-center justify-center border-2 border-dashed border-black/20 rounded-xl py-10 cursor-pointer hover:border-black hover:bg-black/5 transition-colors">
                <Upload size={20} className="text-[#76746F] mb-2" strokeWidth={1.5} />
                <span className="text-sm text-[#76746F]">
                  {uploading ? "Uploading…" : "Click to upload (jpg, png, webp)"}
                </span>
                <input
                  data-testid="file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  className="hidden"
                />
              </label>
            ) : (
              <input
                data-testid="image-url-input"
                value={form.image_url}
                onChange={(e) => update("image_url", e.target.value)}
                placeholder="https://…"
                className="w-full bg-transparent outline-none border-b border-black/20 focus:border-black py-2 font-sans-modern"
              />
            )}

            {form.image_url && (
              <div className="mt-4 relative rounded-lg overflow-hidden border border-black/10">
                <img src={fileUrl(form.image_url)} alt="" className="w-full h-44 object-cover" />
              </div>
            )}
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              data-testid="published-checkbox"
              type="checkbox"
              checked={form.published}
              onChange={(e) => update("published", e.target.checked)}
              className="w-4 h-4 accent-[#1A1A1A]"
            />
            <span className="text-sm text-[#1A1A1A]">Published (visible to readers)</span>
          </label>

          <div className="pt-4 flex items-center gap-3">
            <button
              data-testid="save-story"
              type="submit"
              disabled={busy || uploading}
              className="bg-[#1A1A1A] text-white px-7 py-3 rounded-full hover:bg-[#D4A373] transition-colors text-sm disabled:opacity-60"
            >
              {busy ? "Saving…" : existing ? "Save changes" : "Publish story"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-3 rounded-full text-sm text-[#76746F] hover:text-[#1A1A1A]"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="overline text-[#76746F] mb-2">{label}</p>
      {children}
    </div>
  );
}

function ModeButton({ active, onClick, icon, children, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs transition-colors ${active ? "bg-[#1A1A1A] text-white" : "bg-white border border-black/15 text-[#76746F] hover:text-[#1A1A1A]"}`}
    >
      {icon}
      {children}
    </button>
  );
}
